<template>
  <div
    :class="[
      $style.select,
      opened && $style.opened,
      value !== null && $style.selected,
      error && $style.error,
      disabled && $style.disabled
    ]"
  >
    <button
      :class="$style.button"
      @click.stop="toggleItems"
    >
      {{ value === null ? placeholder : value }}

      <div
        v-once
        :class="$style.arrow"
      >
        <IconArrowDown />
      </div>
    </button>

    <transition name="slide-fade">
      <Error v-if="error">
        {{ error }}
      </Error>
    </transition>

    <div :class="$style.items">
      <a
        v-for="(option, i) in options"
        :key="i"
        tabindex="0"
        :class="[
          $style.item,
          option === value && $style.selected
        ]"
        @click="selectOption(option)"
        @keyup.enter="selectOption(option)"
      >

        {{ option }}

      </a>
    </div>

    <select
      :id="id"
      :class="$style.input"
      @change="selectOption($event.target.value)"
    >
      <option
        v-for="(option, i) in options"
        :key="`option-${i}`"
        :value="option"
        :selected="option === value"
      >
        {{ option }}
      </option>
    </select>
  </div>
</template>

<style lang="scss" module>
.select {
  position: relative;
  width: 100%;

  &.disabled {
    opacity: 0.3;
  }
}

.button {
  position: relative;
  font-size: 1.4rem;
  font-weight: 500;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  width: 100%;
  min-height: 42px;
  padding: 11px 16px;
  cursor: pointer;
  transition: 0.3s border-color;
  text-align: left;
  letter-spacing: 0.7px;
  color: #b7b7b7;
  border: solid 1px #c6c6c6;
  border-radius: 5px;
  outline: none;
  background: none;

  &:hover {
    border-color: #6c6c6c;
  }

  &:focus {
    border-color: #eea2b1;
  }

  .select.selected & {
    color: #3c3c3c;
  }
}

.arrow {
  position: absolute;
  top: 50%;
  right: 16px;
  width: 9px;
  height: 4px;
  margin-top: -2px;
  transition: 0.3s transform;

  svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  path {
    stroke: #c6c6c6;
    transition: 0.3s stroke;

    .button:hover & {
      stroke: #6c6c6c;
    }
  }

  .select.opened & {
    transform: rotate(180deg);
  }
}

.items {
  position: absolute;
  z-index: 2;
  top: 100%;
  left: 0;
  visibility: hidden;
  overflow-y: auto;
  width: 100%;
  max-height: 200px;
  margin-top: 4px;
  transition: 0.3s transform, 0.3s opacity, 0.3s visibility;
  transform: scaleY(0);
  transform-origin: top;
  opacity: 0;
  border: solid 1px #dfdfdf;
  border-radius: 5px;
  background-color: #fff;
  box-shadow: 0 7px 10px 0 rgba(109, 109, 109, 0.07);

  .select.opened & {
    visibility: visible;
    transform: scaleY(1);
    opacity: 1;
  }
}

.item {
  font-size: 1.4rem;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  display: block;
  padding: 10px 12px;
  cursor: pointer;
  letter-spacing: 0.7px;
  color: #4f4f4f;
  outline: none;

  &:hover,
  &.selected,
  &:focus {
    background-color: #f5f5f5;
  }
}

.input {
  width: 0;
  height: 0;
  position: absolute;
  visibility: hidden;
  opacity: 0;
  z-index: -1;
}

.input.error {
  border-color: #e73273;
  caret-color: #e73273;
}
</style>

<script>
import Error from '../others/Error';
import IconArrowDown from '@/images/icons/arrow-down.svg';

export default {
  components: {
    Error,
    IconArrowDown,
  },
  props: {
    id: {
      type: String,
      default: '',
    },
    value: {
      type: String,
      default: null,
    },
    error: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    options: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      opened: false,
    };
  },

  watch: {
    opened(value) {
      if (!value) {
        document.removeEventListener('click', this.closeItems);
      }
      else {
        document.addEventListener('click', this.closeItems);
      }
    },
  },

  methods: {
    selectOption(option) {
      this.$emit('update:value', option);
      this.$emit('change');
      this.closeItems();
    },

    toggleItems() {
      if (this.disabled) {
        return;
      }

      this.opened = !this.opened;
    },

    closeItems() {
      this.opened = false;
    },
  },
};
</script>
